import { useEffect, useMemo, useState } from 'react';

import { Grid } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';

import { useAppDispatch } from '@/store/hooks';
import {
  patientsApiSlice,
  useUpdatePatientOverviewMutation
} from '@/store/slices/api/patientsApiSlice';
import { reportsPatientApiSlice } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { usStateOptions } from '@/utils/constants';
import { API_SLICE } from '@/utils/constants/apiEndpoints';

import { LabelAndValue } from '@/types/custom';
import DatePickerWrapper from '@/ui/DatePickerWrapper';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';
import CustomModal from '@/ui/Modal';
import TextInputWrapper from '@/ui/TextInputWrapper';

import { sexOptions } from '../../TransmissionReportPage/ReportPatientDetails/utils';

import styles from './PatientOverview.module.scss';

export interface PatientOverviewDetails {
  id: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  address1?: string;
  sex?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  phoneNumber?: string;
  homePhone?: string;
  otherPhone?: string;
  mrn2?: string;
  birthDate?: string;
  addressId?: string;
}

interface PatientEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  patient: PatientOverviewDetails;
}

const MAX_LENGTH = 128;
const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const US_PHONE_REGEX = /^(\+1\s?)?\(?[2-9][0-9]{2}\)?[-.\s]?[2-9][0-9]{2}[-.\s]?[0-9]{4}$/;

const PatientEditModal = ({ isOpen, onClose, patient }: PatientEditModalProps) => {
  const [formPatientInfo, setFormPatientInfo] = useState<PatientOverviewDetails>(patient);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [updatePatient] = useUpdatePatientOverviewMutation();

  const dispatch = useAppDispatch();

  const requiredFields: (keyof PatientOverviewDetails)[] = [
    'firstName',
    'lastName',
    'mrn2',
    'sex',
    'birthDate'
  ];

  useEffect(() => {
    if (isOpen) {
      setFormPatientInfo({ ...patient });
      setErrors({});
    }
  }, [isOpen]);

  const validateField = (field: keyof PatientOverviewDetails, value: string, label: string) => {
    let error = '';

    // Required field validation
    if (requiredFields.includes(field) && (!value || value.trim() === '')) {
      error = `${label} is required`;
      setErrors((prev) => ({ ...prev, [field]: error }));
      return;
    }

    // Length validation for address fields
    if (
      ['address1', 'address2', 'city', 'state'].includes(field) &&
      value &&
      value.length > MAX_LENGTH
    ) {
      error = `${label} cannot be longer than ${MAX_LENGTH} characters.`;
    }

    // Zip code validation (only if value is provided)
    if (field === 'zip' && value && value.trim() !== '') {
      if (!US_ZIP_REGEX.test(value)) {
        error = 'Invalid zip code format.';
      }
    }

    // Phone number validation (only if value is provided)
    if (
      ['phoneNumber', 'homePhone', 'otherPhone'].includes(field) &&
      value &&
      value.trim() !== ''
    ) {
      if (!US_PHONE_REGEX.test(value)) {
        error = 'Enter a valid phone number.';
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleFieldChange = (field: keyof PatientOverviewDetails, value: string, label: string) => {
    setFormPatientInfo((prev) => ({ ...prev, [field]: value }));
    validateField(field, value, label);
  };

  const isFormValid = useMemo(() => {
    // Check if all required fields are filled
    const requiredFieldsFilled = requiredFields.every((field) => {
      const value = formPatientInfo[field];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });

    // Check if there are no validation errors
    const noErrors = Object.values(errors).every((msg) => msg === '');

    return requiredFieldsFilled && noErrors;
  }, [formPatientInfo, errors]);

  const handleSave = async () => {
    setIsSaving(true);

    const payload = {
      familyName: formPatientInfo.lastName || '',
      givenName: formPatientInfo.firstName || '',
      preferredName: formPatientInfo.preferredName || '',
      mrn2: formPatientInfo.mrn2 || '',
      birthDate: formPatientInfo.birthDate || '',
      address: {
        id: formPatientInfo.addressId || undefined,
        line1: formPatientInfo.address1 || '',
        line2: formPatientInfo.address2 || '',
        city: formPatientInfo.city || '',
        state: formPatientInfo.state || '',
        postalCode: formPatientInfo.zip || ''
      },
      phoneNumber: formPatientInfo.phoneNumber || '',
      homePhone: formPatientInfo.homePhone || '',
      otherPhone: formPatientInfo.otherPhone || '',
      sex: formPatientInfo.sex || ''
    };

    try {
      await updatePatient({ id: patient.id, body: payload }).unwrap();

      dispatch(patientsApiSlice.util.invalidateTags([{ type: API_SLICE.Patient, id: patient.id }]));
      dispatch(
        reportsPatientApiSlice.util.invalidateTags([{ type: 'ReportPatient', id: patient.id }])
      );

      notifications.show({
        title: 'Success',
        message: 'Patient information updated successfully.',
        color: 'green'
      });

      onClose();
    } catch (error) {
      console.error('Failed to update patient:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update patient information. Please try again.',
        color: 'red'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const renderTextInput = (
    label: string,
    field: keyof PatientOverviewDetails,
    required: boolean = false
  ) => (
    <TextInputWrapper
      className={styles.inputText}
      label={label}
      value={formPatientInfo[field] || ''}
      onChange={(e) => handleFieldChange(field, e.target.value, label)}
      required={required}
      error={errors[field]}
    />
  );

  const renderSelect = (
    label: string,
    options: LabelAndValue[],
    field: keyof PatientOverviewDetails,
    value: string | undefined,
    required: boolean = false
  ) => {
    const selected = options.find((opt) => opt.value === value);
    return (
      <Dropdown
        label={label}
        options={options}
        value={selected}
        onChange={(option) => handleFieldChange(field, (option as LabelAndValue).value, label)}
        placeholder={`Select ${label}`}
        labelTextProps={{ variant: 'regular' }}
        required={required}
      />
    );
  };

  return (
    <CustomModal
      modalProps={{
        opened: isOpen,
        onClose,
        size: 'xl',
        closeOnClickOutside: false,
        closeOnEscape: false
      }}
      header={{ title: 'Edit Patient Overview' }}
      primaryButton={{
        text: 'Save Changes',
        isLoading: isSaving,
        onClick: handleSave,
        isDisabled: !isFormValid || isSaving
      }}
      secondaryButton={{
        text: 'Cancel',
        isDisabled: isSaving,
        onClick: onClose
      }}>
      <div className={styles.patientSection}>
        <Grid mt="md">
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput('First Name', 'firstName', true)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput('Last Name', 'lastName', true)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            {renderTextInput('Middle Name', 'preferredName')}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>{renderTextInput('MRN', 'mrn2', true)}</Grid.Col>

          <Grid.Col span={{ base: 12, sm: 2 }}>
            {renderSelect('Sex', sexOptions, 'sex', formPatientInfo.sex, true)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>
            <LabelText label="Date of Birth" required variant="regular" boxProps={{ mb: 4 }} />
            <DatePickerWrapper
              inputProps={{ placeholder: 'MM/DD/YYYY', required: true }}
              datePickerProps={{
                id: 'dob',
                selected: formPatientInfo.birthDate ? new Date(formPatientInfo.birthDate) : null,
                onChange: (date: Date | null) =>
                  handleFieldChange(
                    'birthDate',
                    date ? dayjs(date).format('MM/DD/YYYY') : '',
                    'Date of Birth'
                  ),
                required: true,
                isClearable: false
              }}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>{renderTextInput('Address 1', 'address1')}</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 3 }}>{renderTextInput('Address 2', 'address2')}</Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>{renderTextInput('City', 'city')}</Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderSelect('State', usStateOptions, 'state', formPatientInfo.state)}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>{renderTextInput('Zip', 'zip')}</Grid.Col>

          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderTextInput('Mobile Phone', 'phoneNumber')}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderTextInput('Home Phone', 'homePhone')}
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            {renderTextInput('Other Phone', 'otherPhone')}
          </Grid.Col>
        </Grid>
      </div>
    </CustomModal>
  );
};

export default PatientEditModal;
